export const DANGER_COLOR = '#dc2626';
export const LARGE_COLOR = '#111827';

type QuillOp = {
  insert?: string | Record<string, unknown>;
  attributes?: Record<string, unknown> | null;
};

type QuillDeltaLike = {
  ops?: QuillOp[] | null;
};

const MARKERS: Array<{
  open: string;
  close: string;
  render: (text: string) => string;
}> = [
  { open: '**', close: '**', render: (text) => `<strong>${text}</strong>` },
  {
    open: '[红色]',
    close: '[/红色]',
    render: (text) =>
      `<strong><span style="color:${DANGER_COLOR};">${text}</span></strong>`,
  },
  {
    open: '[大字]',
    close: '[/大字]',
    render: (text) =>
      `<strong><span class="ql-size-large" style="color:${LARGE_COLOR};">${text}</span></strong>`,
  },
];

export function escapeEditorHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderLine(line: string): string {
  let html = '';
  let cursor = 0;

  while (cursor < line.length) {
    const marker = MARKERS.find((item) => line.startsWith(item.open, cursor));
    if (!marker) {
      html += escapeEditorHtml(line[cursor]);
      cursor += 1;
      continue;
    }

    const contentStart = cursor + marker.open.length;
    const contentEnd = line.indexOf(marker.close, contentStart);
    if (contentEnd < 0) {
      html += escapeEditorHtml(line.slice(cursor));
      break;
    }

    const text = line.slice(contentStart, contentEnd);
    if (!text) {
      html += escapeEditorHtml(line.slice(cursor, contentEnd + marker.close.length));
    } else {
      html += marker.render(escapeEditorHtml(text));
    }
    cursor = contentEnd + marker.close.length;
  }

  return html;
}

export function taskMarkupToEditorHtml(value: string): string {
  return value.split(/\r\n|\r|\n/).map(renderLine).join('<br>');
}

function normalizeColor(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '') : '';
}

function renderQuillInsert(op: QuillOp): string {
  if (typeof op.insert !== 'string') return '';

  const attributes = op.attributes ?? {};
  const color = normalizeColor(attributes.color);
  const size = typeof attributes.size === 'string' ? attributes.size : '';
  const isBold = attributes.bold === true;

  return op.insert.split('\n').map((part, index, parts) => {
    if (part === '') {
      return index < parts.length - 1 ? '\n' : '';
    }

    let text = part;
    if (size === 'large') {
      text = `[大字]${text}[/大字]`;
    } else if (color === DANGER_COLOR) {
      text = `[红色]${text}[/红色]`;
    } else if (isBold) {
      text = `**${text}**`;
    }

    return index < parts.length - 1 ? `${text}\n` : text;
  }).join('');
}

export function quillDeltaToTaskMarkup(delta: QuillDeltaLike): string {
  return (delta.ops ?? [])
    .map(renderQuillInsert)
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
}
